import type { ReactNode } from "react";
import { AnimatePresence, m } from "framer-motion";
import type { ConnectionItemProps } from "@wayflyer/flyui";
import { AddNewButton, ConnectionItem, Flex, Spinner } from "@wayflyer/flyui";
import { IconBank24Line } from "@wayflyer/flyui-icons/24/line";
import type { BankConnectionLogo } from "bankconnect";
import { useBankConnectionLogoResolvers, useBankConnectionLogos, useBankConnectWithBasiq } from "bankconnect";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { COMMON_MESSAGES } from "../../../../components/common-messages";
import { UPLOAD_STATEMENTS_BANK_GROUP_LOCATION_STATE } from "../../../../components/constants";
import { composePath, PAGE_TYPE } from "../../../../hooks/navigation-next/use-funding-application-navigation-next";
import { useBankingSources } from "../../../../queries/bank-connect/queries";
import { useBankConnectContext } from "../context/use-bank-connect-context";
import { MESSAGES } from "../message-definitions";

import { SmallImage } from "./styled";

const BankLogoDisplay = ({ logoState }: { logoState: BankConnectionLogo }) => {
  if (!logoState || logoState.loading) {
    return <Spinner size={24} variant="monochrome" />;
  } else if (!logoState.loading && logoState.couldLoad && logoState.data) {
    const srcPrefix = logoState.type === "url" ? "" : "data:image/png;base64,";
    return <SmallImage src={`${srcPrefix}${logoState.data}`} alt="" />;
  } else {
    return <IconBank24Line />;
  }
};

type BankConnectionListProps = {
  formContent: ReactNode | null;
  onAddAnother: () => void;
  statementsUploadPath?: string;
};

export const BankConnectionList = ({
  formContent,
  onAddAnother,
  statementsUploadPath = composePath(PAGE_TYPE.BANK_STATEMENTS_UPLOAD),
}: BankConnectionListProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const bankConnectSchema = useBankConnectContext();
  const {
    plaidConnectProps: { getInstitutionLogoPath },
    basiqConnectProps,
  } = bankConnectSchema;
  const { navigate } = useSpaNavigate("FundingApp");

  const { basiqClient } = useBankConnectWithBasiq(basiqConnectProps);

  const logoResolvers = useBankConnectionLogoResolvers({ getInstitutionLogoPath, basiqClient });

  const { data: bankingSources } = useBankingSources(customerDomain);
  const { logoLoadingStates } = useBankConnectionLogos({
    bankConnections: bankingSources.bankingDataSources?.length ? bankingSources.bankingDataSources : [],
    resolvers: logoResolvers,
  });

  const accountResourceItems: ConnectionItemProps[] | undefined = bankingSources.bankingDataSources
    ?.sort((a, b) => (a.institutionName > b.institutionName ? 1 : a.institutionName === b.institutionName ? 0 : -1))
    .map((connection) => {
      const pullEnabledAccounts = connection.accounts
        ? connection.accounts.filter((account) => account.pull_enabled)
        : [];

      return {
        testId: connection.id,
        label: connection.institutionName,
        logo: <BankLogoDisplay logoState={logoLoadingStates[connection.institutionID]} />,
        subtitle: pullEnabledAccounts.length && (
          <ul>
            {pullEnabledAccounts.map((account) => (
              <li key={account.id}>
                {intl.formatMessage(
                  { id: "funding-application: account ending", defaultMessage: "Account ending {mask}" },
                  { mask: account.mask }
                )}
              </li>
            ))}
          </ul>
        ),
        badgeStatus: "positive",
        badgeText: intl.formatMessage(COMMON_MESSAGES.connected),
      } satisfies ConnectionItemProps;
    });

  const statementResourceItems: ConnectionItemProps[] | null =
    (bankingSources.bankingStatementsGroups &&
      bankingSources.bankingStatementsGroups
        ?.sort((a, b) => (a.name > b.name ? 1 : a.name === b.name ? 0 : -1))
        .map((group) => {
          return {
            testId: group.groupUuid,
            label: group.name,
            logo: <IconBank24Line />,
            subtitle: group.previouslyUploadedStatements && (
              <ul>
                {group.previouslyUploadedStatements?.map((statement, index) => (
                  <li key={statement.fileName + index}>{statement.fileName}</li>
                ))}
              </ul>
            ),
            actions: [
              {
                content: intl.formatMessage(MESSAGES.uploadMoreFiles),
                onAction: () =>
                  navigate(statementsUploadPath, {
                    state: { [UPLOAD_STATEMENTS_BANK_GROUP_LOCATION_STATE]: group.groupUuid },
                  }),
              },
            ],
          } satisfies ConnectionItemProps;
        })) ||
    null;

  return (
    (accountResourceItems || statementResourceItems) && (
      <Flex direction="column" width="100%" gap="2">
        {[...(accountResourceItems ?? []), ...(statementResourceItems ?? [])].map((item, idx) => (
          <ConnectionItem key={idx} {...item} />
        ))}

        <AnimatePresence initial={false}>
          {!formContent && (
            <Flex height="var(--sizes-spacing-16)">
              <AddNewButton orientation="row-centered" onClick={onAddAnother}>
                {intl.formatMessage(COMMON_MESSAGES.addAnother)}
              </AddNewButton>
            </Flex>
          )}

          {formContent && (
            <m.div
              key="expanded-content"
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              variants={{
                expanded: { height: "auto", opacity: 1, transitionEnd: { overflow: "visible" } },
                collapsed: { height: 0, opacity: 0, overflow: "hidden" },
              }}
              transition={{ duration: 0.3 }}
            >
              {formContent}
            </m.div>
          )}
        </AnimatePresence>
      </Flex>
    )
  );
};
